import $ from 'jquery'
import _ from 'underscore'
import {PaperOrientation} from '../../../pdf/constants'
import messageResource from '../../../messageResource'
import {
    ChartPDFFooters,
    ChartPDFParameters,
    ChartPDFPlaceholderConstructors,
    ChartPDFStyles,
    ChartPDFTemplateConstructors
} from './ChartPDFDefinitionPartsConstructors'

class ChartPDFDefinitionBuilder {
    constructor(logoPicture, chartType, paperSize, paperOrientation, grandTotalValue, headerTexts, valueFormatter, tableDefinitionBuilder) {
        this.chartType = chartType

        this.paperSize = paperSize
        this.paperOrientation = paperOrientation

        this.allParams = {}
        $.extend(true, this.allParams, ChartPDFParameters) // need to prevent changes parameters by pdfmake
        this.orientationParams = this.allParams[paperOrientation]
        this.paperSizeParams = this.orientationParams[paperSize]
        this.chartTypeParams = this.paperSizeParams[chartType]

        this.chartTypeName = headerTexts.chartTypeName
        this.filterTexts = headerTexts.filterTexts
        this.dateRangeDescription = headerTexts.dateRangeDescription
        this.grandTotalDescription = headerTexts.grandTotalDescription

        this.valueFormatter = valueFormatter

        this.grandTotalValue = grandTotalValue

        this.logoPicture = logoPicture

        this.templateCtrs = ChartPDFTemplateConstructors

        this.tableDefinitionBuilder = tableDefinitionBuilder

        return this
    }

    getTable(dataRows, margins, tableStyle) {
        return this.tableDefinitionBuilder.getTable(dataRows, margins, tableStyle)
    }

    getDataRows(pageDataList, showDataList, showPercentsList, tableValueStyles, rowDataMaxLens) {
        return this.tableDefinitionBuilder.getDataRows(pageDataList, showDataList, showPercentsList, tableValueStyles, rowDataMaxLens)
    }


    /**
     * Crete new doc definition with current page parameters and with footer
     * @returns {{}}
     */
    createNewDocDefinitionWithFooter() {

        var docDefinition = {}

        docDefinition.pageSize = this.paperSizeParams.pageSize
        docDefinition.pageOrientation = this.orientationParams.pageOrientation
        docDefinition.pageMargins = this.orientationParams.pageMargins

        docDefinition.content = []
        docDefinition.footer = ChartPDFFooters.simpleFooter()
        docDefinition.styles = ChartPDFStyles

        return docDefinition
    }

    /**
     * Method add header to pdf page doc definition
     * @param docDefinition - current doc definition
     * @param showLogo - true if need add logo to page
     * @param includeOtherUsers - if need to add row in header. TODO looks like incorrect parameter
     */
    addHeader(docDefinition, showLogo, includeOtherUsers) {
        let placeCtrs = ChartPDFPlaceholderConstructors

        let filterTextMaxLen = this.paperSizeParams.filterViewTextLen
        let filterViewElement
        if (includeOtherUsers) {
            filterViewElement = this.templateCtrs.filterViewWithThreeRows(
                {
                    ...this.orientationParams.filterViewParameters,
                    ...this.orientationParams.filterViewParametersWithThreeRows,
                },
                this._cutLongLabels(this.filterTexts.firstRow, filterTextMaxLen),
                messageResource.getMessage('report.chart_pdf.table_header.data_of_not_assigned_users_shown'),
                this._cutLongLabels(this.filterTexts.secondRow, filterTextMaxLen))
        } else {
            filterViewElement = this.templateCtrs.filterView(
                {
                    ...this.orientationParams.filterViewParameters,
                    ...this.orientationParams.filterViewParametersWithTwoRows,
                },
                this._cutLongLabels(this.filterTexts.firstRow, filterTextMaxLen),
                this._cutLongLabels(this.filterTexts.secondRow, filterTextMaxLen))
        }
        let logoElement = showLogo ? this.templateCtrs.logoPicture(this.logoPicture.imageBase64, this._calculatePDFLogoDimensions(this.logoPicture)) : ''

        const chartInfoParameters = showLogo ? this.paperSizeParams.chartInfoParameters.withLogo : this.paperSizeParams.chartInfoParameters.withoutLogo

        const chartInfoElement = this.makeChartInformationHeader(chartInfoParameters, logoElement)
        let headerElement
        let appender = _.bind(this._appendContentElement, this, docDefinition)

        if (this.paperOrientation === PaperOrientation.PORTRAIT) {
            headerElement = placeCtrs.headerPortrait(chartInfoElement)
            appender(headerElement)
            appender(filterViewElement)
        } else {
            headerElement = placeCtrs.headerLandscape(chartInfoElement, filterViewElement)
            appender(headerElement)
        }
    }

    makeChartInformationHeader(chartInfoParameters, logoElement) {
        return this.templateCtrs.chartInformation(chartInfoParameters, logoElement, this.chartTypeName, this.dateRangeDescription,
            this.grandTotalDescription, this.valueFormatter(this.grandTotalValue))
    }

    /**
     * method adds image and data on page depends page parameters
     * @param docDefinition - doc definition of pdf
     * @param imageBase64 - image
     * @param pageDataList - list of data for one page
     * @param showDataList - if need to show data
     * @param showPercentsList - if need show percent values
     * @param smallTable - if data list with small rows
     */
    addImageAndData(
        docDefinition,
        imageBase64,
        pageDataList,
        showDataList,
        showPercentsList,
        smallTable,
    ) {
        const placeCtrs = ChartPDFPlaceholderConstructors

        const imageParams = this.chartTypeParams.imageParams.singleImage
        const tableMargins = this.chartTypeParams.dataTableMargins
        const tableStyles = smallTable ? this.allParams.dataTableCommonStyles.small : this.allParams.dataTableCommonStyles.big
        const rowDataMaxLens = smallTable ? this.chartTypeParams.rowDataMaxLen.fit : this.chartTypeParams.rowDataMaxLen.normal

        const image = this.templateCtrs.imageWithSizes(imageParams.sizes, imageBase64)

        const rows = this.getDataRows(pageDataList, showDataList, showPercentsList, tableStyles.tableValueStyles, rowDataMaxLens)

        const table = this.getTable(rows, tableMargins, tableStyles.tableStyle)

        this._appendContentElement(docDefinition, placeCtrs.singleImage(image, imageParams.margins))
        this._appendContentElement(docDefinition, table)
    }

    /**
     * Method add double image on one page depends of parameters
     * @param docDefinition - current doc definition
     * @param firstImageBase64 - firstImage
     * @param secondImageBase64 - secondImage
     */
    addDoubleImagesVertical(docDefinition, firstImageBase64, secondImageBase64) {
        this._addDoubleImagesInternal(docDefinition, firstImageBase64, secondImageBase64, (imageFirst, imageSecond, margins) => {
            const placeCtrs = ChartPDFPlaceholderConstructors
            this._appendContentElement(docDefinition, placeCtrs.singleImage(imageFirst, margins))
            this._appendContentElement(docDefinition, placeCtrs.singleImage(imageSecond, margins))
        })
    }

    /**
     * Method add double images with horizontal layout
     * @param docDefinition - current dd
     * @param firstImageBase64 - firstImage
     * @param secondImageBase64 - secondImage
     */
    addDoubleImagesHorizontal(docDefinition, firstImageBase64, secondImageBase64) {
        this._addDoubleImagesInternal(docDefinition, firstImageBase64, secondImageBase64, (imageFirst, imageSecond, margins) => {
            this._appendContentElement(docDefinition, ChartPDFPlaceholderConstructors.horizontalDoubleImages(imageFirst, imageSecond, margins))
        })
    }

    _addDoubleImagesInternal(docDefinition, firstImageBase64, secondImageBase64, appender = (imageFirst, imageSecond, margins) => {}) {
        const imageParams = this.chartTypeParams.imageParams.doubleImage

        const imageFirstElement = this.templateCtrs.imageWithSizes(imageParams.sizes, firstImageBase64)
        let imageSecondElement
        if (secondImageBase64) {
            imageSecondElement = this.templateCtrs.imageWithSizes(imageParams.sizes, secondImageBase64)
        } else {
            imageSecondElement = this.templateCtrs.fakeImage()
        }

        appender(imageFirstElement, imageSecondElement, imageParams.margins)
    }

    /**
     * Add only table for page
     * @param docDefinition - current doc definition
     * @param pageDataList - list of data for current page
     * @param showDataList - if need to show data
     * @param showPercentsList - if need to show percents
     * @param smallTable - small version of table
     */
    addOnlyTableToPage(docDefinition, pageDataList, showDataList, showPercentsList, smallTable) {
        const tableStyles = smallTable ? this.allParams.dataTableCommonStyles.small : this.allParams.dataTableCommonStyles.big
        const rowDataMaxLens = smallTable ? this.chartTypeParams.rowDataMaxLen.fit : this.chartTypeParams.rowDataMaxLen.normal
        const rows = this.getDataRows(pageDataList, showDataList, showPercentsList, tableStyles.tableValueStyles, rowDataMaxLens)

        const table = this.getTable(rows, this.chartTypeParams.dataTableMargins, tableStyles.tableStyle)
        this._appendContentElement(docDefinition, table)
    }

    /**
     * Add only image to page
     * @param docDefinition - current doc definition
     * @param imageBase64 - image in base64
     */
    addOnlyImageToPage(docDefinition, imageBase64) {
        var imageParams = this.chartTypeParams.imageParams.singleImage
        var image = this.templateCtrs.imageWithSizes(imageParams.sizes, imageBase64)
        this._appendContentElement(docDefinition, ChartPDFPlaceholderConstructors.singleImage(image, imageParams.margins))
    }

    /**
     * Add to doc definition page breaker
     * @param docDefinition - current doc definition
     */
    addPageBreaker(docDefinition) {
        this._appendContentElement(docDefinition, ChartPDFPlaceholderConstructors.pageBreaker())
    }

    /**
     * Append element to content list in doc definition
     * @param dd - doc def
     * @param element - element fo add
     * @private
     */
    _appendContentElement(dd, element) {
        dd.content.push(element)
    }


    _cutLongLabels(label, maxLen) {
        if (label.length > maxLen) {
            return label.substr(0, maxLen - 3) + '...'
        }
        return label
    }

    /**
     * method calculate logo image dimensions for fit it to placeholder sizes and save ration
     * @param logoPicture logo picture (logo + sizes in pixels)
     * @returns {{w: number, h: number, mL: number, mT: number}}
     * @private
     */
    _calculatePDFLogoDimensions(logoPicture) {

        var maxW = ChartPDFParameters.logoPlaceholderSizes.width
        var maxH = ChartPDFParameters.logoPlaceholderSizes.height

        var widthInPix = logoPicture.width
        var heightInPix = logoPicture.height

        var ratioForPlace = maxW / maxH
        var rationForPixSizes = widthInPix / heightInPix

        var coeff = 0 // coeff for calculate pdf logo dimensions
        // if ration for placeholder(w/h) less than for image, then need to adjust max height of image or width otherwise
        if (ratioForPlace > rationForPixSizes) {
            coeff = heightInPix / maxH
        } else {
            coeff = widthInPix / maxW
        }
        var wInPDF = widthInPix / coeff
        var hInPDF = heightInPix / coeff
        return {
            w: wInPDF,
            h: hInPDF,
            mL: (maxW - wInPDF) / 2,
            mT: 0
        }
    }
}

export default ChartPDFDefinitionBuilder


// WEBPACK FOOTER //
// ./web/js/reports/charts/pdfgenerator/ChartPDFDefinitionBuilder.js